import { setCoords } from 'app/utils/general'
import {
  getElementByCFI,
  getIframes,
  isInSecondIframe,
} from 'app/services/utils/annotation.utils'
import { markSize } from 'app/constants/annotation.constants'
import { GroupPosition } from 'app/types/annotation.types'

const minOffset = { top: 20, left: 10 } // min popup offsets from container edges
const minPointerOffset = 23 // min popup pointer offset needed for pointer edges to stay in popup
const popupTopOffset = 25 // popup offset from select
const minMarkOffset = 40 // min mark offset to be visible on page

const calculateMarkPosition = (
  range: Range,
  container: HTMLElement,
  isSecondPage: boolean
) => {
  const rangeRect = range.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  const rangeMiddleY = rangeRect.top + rangeRect.height / 2

  let { left } = containerRect
  if (isSecondPage) left += containerRect.width - markSize

  return {
    top: containerRect.top,
    middleY: rangeMiddleY,
    left,
  }
}

type ElementPosition = {
  pointerOffset?: number
  top: number
  left: number
  isReversed: boolean
}

const calculatePopupPosition = (
  range: Range,
  popup: HTMLElement,
  container: HTMLElement,
  popupOffset: number
): ElementPosition => {
  let pointerOffset

  const rangeRect = range.getBoundingClientRect()
  const popupRect = popup.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  if (containerRect.width <= 0) containerRect.width = window.innerWidth

  const rangeMiddleX = rangeRect.left + rangeRect.width / 2

  let top = rangeRect.top - popupTopOffset - popupRect.height
  let left = rangeMiddleX - popupRect.width / 2

  // popup will be shown bellow selected text
  const isReversed = top < minOffset.top
  if (isReversed) top = rangeRect.top + popupTopOffset + rangeRect.height

  // popup right position is bigger than container width
  if (
    left + popupRect.width / 2 >
    containerRect.width + containerRect.left - minOffset.left
  ) {
    left = containerRect.width - popupRect.width - minOffset.left

    const pointerPosition = rangeMiddleX - left

    pointerOffset =
      pointerPosition > popupRect.left + popupRect.width - minPointerOffset
        ? popupRect.width - minPointerOffset
        : pointerPosition
  }

  // popup left position is smaller than min left offset
  if (left < minOffset.left) {
    left = minOffset.left
    const pointerPosition = rangeMiddleX - minOffset.left

    pointerOffset =
      pointerPosition < minPointerOffset ? minPointerOffset : pointerPosition
  }

  if (top + popupRect.height + popupTopOffset > containerRect.height) {
    top = rangeRect.top + rangeRect.height / 2
  }

  if (popupOffset) left += popupOffset

  return {
    top,
    left,
    pointerOffset,
    isReversed,
  }
}

const setPointerPosition = (
  pointer: HTMLElement,
  position: ElementPosition
) => {
  if (position.isReversed) {
    pointer.classList.add('annotation-popup__container--reversed')
  } else {
    pointer.classList.remove('annotation-popup__container--reversed')
  }

  const pointerLeft = position.pointerOffset
    ? `${position.pointerOffset}px`
    : '50%'

  pointer.style.setProperty('--pointer-offset', pointerLeft)
}

const placePopup = (
  range: Range,
  popup: HTMLElement,
  container: HTMLElement,
  popupOffset: number
) => {
  const coords = calculatePopupPosition(range, popup, container, popupOffset)

  setPointerPosition(popup, coords)
  setCoords(popup, coords)
}

const placeMarkPopup = (markPosition: GroupPosition, popup: HTMLElement) => {
  const markTop = parseFloat(markPosition.top)
  const markLeft = markPosition.left

  const popupRect = popup.getBoundingClientRect()
  const containerRect = document.body.getBoundingClientRect()
  const isSecondPage = markLeft > containerRect.width / 2

  let left = markLeft + minMarkOffset * 2
  let top = markTop + markSize / 2 - popupRect.height / 2

  if (left + popupRect.width > containerRect.width) {
    left = markLeft - minMarkOffset * 2 - popupRect.width
  }

  if (top < minOffset.top) top = markTop
  if (top + popupRect.height > containerRect.height) {
    top = markTop - popupRect.height - minMarkOffset
  }

  if (isSecondPage) left += containerRect.width

  setCoords(popup, { top, left })
}

const placeMark = (
  cfi: string,
  position: { top: number; left: number },
  container: HTMLElement | Document = document
) => {
  const currentMark = getElementByCFI(cfi, 'a', container)
  if (!currentMark) return
  setCoords(currentMark, position)

  currentMark.style.display = 'block'
}

const getMarkPosition = (container: HTMLElement, isSecondPage: boolean) => {
  // eslint-disable-next-line prefer-const
  let { left, width } = container.getBoundingClientRect()

  if (isSecondPage) left += width
  if (left < minMarkOffset) left = minMarkOffset

  return left
}

// Calculate mark position
const calculateMarkLeft = (cfi: string) => {
  const iframes = getIframes()
  const containerRect = iframes[0]?.body.getBoundingClientRect()

  let left = containerRect?.left ?? 0

  if (containerRect && isInSecondIframe(cfi, iframes)) {
    left = containerRect.width - markSize
  }

  return left
}

// Get setted mark position
const getSettedMarkPosition = (
  cfi?: string,
  container: HTMLElement | Document = document
) => {
  const mark = getElementByCFI(cfi, 'a', container)
  if (!mark) return { top: 0, left: 0 }
  const top = parseFloat(mark.style?.top) || 0
  const left = parseFloat(mark.style?.left) || 0

  return { top, left }
}

export default {
  setCoords,
  calculateMarkPosition,
  calculatePopupPosition,
  setPointerPosition,
  placePopup,
  placeMarkPopup,
  placeMark,
  getMarkPosition,
  calculateMarkLeft,
  getSettedMarkPosition,
}
